﻿import { makeAutoObservable, runInAction } from 'mobx';
import type { CategoryId } from '@shared/config/categories';
import type { BattleDto, BattleSnapshot, BattleEvent } from '@shared/api';
import type { BattleChannel, BattleSocketHandlers } from '@shared/lib/battle-socket';

type QuestionSource = NonNullable<BattleSnapshot['question']>;
export type ServerBattleQuestion = QuestionSource & { options: readonly string[] };
export type BattleAnswerMemo = { titleId: string; remainingMs: number };
type Transport = {
  create: (kinds: readonly string[], displayName?: string) => Promise<BattleDto>;
  get: (code: string) => Promise<BattleDto>;
  join: (code: string, displayName?: string) => Promise<BattleDto>;
  snapshot: (code: string) => Promise<BattleSnapshot>;
  question: (source: QuestionSource) => Promise<ServerBattleQuestion>;
  difficulty?: (id: string) => Promise<number>;
  open: (code: string, handlers: BattleSocketHandlers) => BattleChannel;
  actionId?: () => string;
  readAnswer?: (code: string, key: string) => BattleAnswerMemo | undefined;
  writeAnswer?: (code: string, key: string, value: BattleAnswerMemo) => void;
};
const kinds = { movies: 'film', series: 'series', games: 'game', anime: 'anime' };
const categoriesByKind: Record<string, CategoryId> = {
  film: 'movies',
  series: 'series',
  game: 'games',
  anime: 'anime',
};
const allCategories: CategoryId[] = ['movies', 'series', 'games', 'anime'];
const categoriesFor = (battle: BattleDto) =>
  battle.kinds?.length
    ? battle.kinds.flatMap((kind) => (categoriesByKind[kind] ? [categoriesByKind[kind]] : []))
    : battle.kind
      ? categoriesByKind[battle.kind]
        ? [categoriesByKind[battle.kind]!]
        : []
      : allCategories;
const finished = (battle: BattleDto) =>
  !!(
    battle.finishedAt ||
    battle.winnerLogin ||
    battle.loserLogin ||
    ['finished', 'draw', 'cancelled', 'aborted'].includes(battle.status)
  );

export class BattleRoomStore {
  phase: 'setup' | 'lobby' | 'loading' | 'question' | 'reveal' | 'finished' = 'setup';
  battle: BattleDto | null = null;
  question: ServerBattleQuestion | null = null;
  answers: BattleSnapshot['answers'] = {};
  round = 0;
  connecting = false;
  syncing = false;
  starting = false;
  connection: 'idle' | 'connecting' | 'open' | 'closed' = 'idle';
  reconnectBlocked = false;
  retryGeneration = 0;
  error: string | null = null;
  answerError: string | null = null;
  name = '';
  frameReady = false;
  remainingMs = 0;
  revealRemainingMs = 0;
  correctTitleId: string | undefined;
  readyPending = false;
  private serverAnsweredCount: number | undefined;
  private offset = 0;
  private nextQuestionAt = 0;
  private roundPhase = 'question';
  pendingAnswer: { questionId: string; titleId: string; actionId: string } | null = null;
  private revision = 0;
  private streamVersion = 0;
  private socketSerial = 0;
  private login = '';
  private questionKey = '';
  private deadline = 0;
  private request: Promise<void> | null = null;
  private answerRetry: Promise<void> | null = null;
  private channel: BattleChannel | null = null;
  private roundRequest: { key: string; request: Promise<void> } | null = null;
  private readonly transport: Transport;
  private readonly viewer: () => string | undefined;
  constructor(transport: Transport, viewer: () => string | undefined) {
    this.transport = transport;
    this.viewer = viewer;
    makeAutoObservable<this, 'transport' | 'viewer' | 'request' | 'answerRetry' | 'channel' | 'roundRequest'>(
      this,
      { transport: false, viewer: false, request: false, answerRetry: false, channel: false, roundRequest: false },
      { autoBind: true },
    );
  }
  get code() {
    return this.battle?.inviteCode ?? '';
  }
  get categories() {
    return this.battle ? categoriesFor(this.battle) : [];
  }
  get selfId() {
    return this.login;
  }
  get ownerLogin() {
    return this.login;
  }
  get connectionAttempt() {
    return this.socketSerial;
  }
  get isCreator() {
    return this.battle?.hostLogin === this.selfId;
  }
  get players() {
    return [this.battle?.hostLogin, this.battle?.guestLogin]
      .filter((id): id is string => !!id)
      .map((id) => ({
        id,
        name:
          (id === this.battle?.hostLogin ? this.battle.hostDisplayName : this.battle?.guestDisplayName) ||
          (id === this.selfId ? this.name || 'Вы' : id.startsWith('guest_') ? 'Гость' : id),
        ready: id === this.battle?.hostLogin ? this.battle.hostReady : this.battle?.guestReady,
      }));
  }
  get self() {
    return this.players.find((player) => player.id === this.selfId);
  }
  get opponent() {
    return this.players.find((player) => player.id !== this.selfId);
  }
  get answeredCount() {
    return (
      this.serverAnsweredCount ??
      Object.keys(this.answers).filter((id) => this.players.some((player) => player.id === id)).length
    );
  }
  get canStart() {
    return (
      this.phase === 'lobby' &&
      this.isCreator &&
      this.players.length === 2 &&
      this.players.every((player) => player.ready) &&
      this.connection === 'open' &&
      !this.starting
    );
  }
  get selectedAnswer() {
    const titleId = this.answers[this.selfId]?.titleId ?? this.pendingAnswer?.titleId;
    const index = titleId ? this.question?.optionTitleIds.indexOf(titleId) : undefined;
    return index !== undefined && index >= 0 ? index : null;
  }
  get answered() {
    return !!this.answers[this.selfId] || !!this.pendingAnswer;
  }
  get timedOut() {
    return this.deadline > 0 && this.remainingMs <= 0;
  }
  get roundsPlayed() {
    return this.battle?.questionsPlayed ?? 0;
  }
  get outcome() {
    return this.battle?.winnerLogin
      ? this.battle.winnerLogin === this.selfId
        ? 'won'
        : 'lost'
      : this.battle?.status === 'draw' || this.battle?.loseReason === 'draw'
        ? 'draw'
        : null;
  }
  get resultTitle() {
    return this.outcome === 'won'
      ? 'Вы победили!'
      : this.outcome === 'lost'
        ? 'Победил соперник'
        : this.outcome === 'draw'
          ? 'Ничья'
          : 'Батл завершён';
  }
  get resultDescription() {
    if (this.battle?.status === 'aborted') return this.error ?? 'Батл закрыт.';
    return this.battle?.loseReason === 'timeout'
      ? 'Время на ответ закончилось.'
      : this.battle?.loseReason === 'disconnect'
        ? 'Один из игроков потерял соединение.'
        : this.battle?.loseReason === 'forfeit'
          ? 'Один из игроков завершил участие.'
          : this.outcome === 'draw'
            ? 'Оба игрока ошиблись в одном вопросе.'
            : '';
  }
  private active(revision: number, login: string | undefined, code = this.code) {
    return revision === this.revision && login === this.viewer() && code === this.code;
  }
  private applyBattle(battle: BattleDto) {
    if (this.battle && (battle.id !== this.battle.id || battle.inviteCode !== this.code)) return;
    if (this.phase === 'finished' && !finished(battle)) return;
    this.battle = battle;
    if (finished(battle)) {
      this.phase = 'finished';
      this.pendingAnswer = null;
      this.starting = false;
      this.answerError = null;
      this.error = null;
      this.socketSerial += 1;
      this.channel?.close();
      this.channel = null;
      this.connection = 'idle';
    } else if (battle.status === 'lobby') this.phase = 'lobby';
    else if (this.phase === 'lobby') this.phase = 'loading';
    if (battle.status !== 'lobby') this.starting = false;
  }
  async enter(available: readonly CategoryId[], name: string, selected: readonly CategoryId[], code?: string) {
    if (this.connecting || !name.trim()) return false;
    const login = this.viewer();
    if (!login) {
      this.error = 'Не удалось определить игровую сессию. Обновите страницу.';
      return false;
    }
    const chosen = selected.filter((id) => available.includes(id));
    if (!code && !chosen.length) {
      this.error = 'Выберите хотя бы одну категорию.';
      return false;
    }
    this.leave();
    const revision = this.revision;
    this.connecting = true;
    this.login = login;
    try {
      let battle: BattleDto;
      if (code) {
        battle = await this.transport.get(code);
        if (revision !== this.revision || login !== this.viewer()) return false;
        const roomCategories = categoriesFor(battle);
        if (!roomCategories.length || roomCategories.some((id) => !available.includes(id))) {
          const e = new Error('Для этого батла включите его категории в настройках.');
          e.name = 'BattleCategorySelectionError';
          throw e;
        }
        if (battle.hostLogin !== login && battle.guestLogin !== login)
          battle = await this.transport.join(code, name.trim());
      } else
        battle = await this.transport.create(
          chosen.map((id) => kinds[id]),
          name.trim(),
        );
      if (revision !== this.revision || login !== this.viewer()) return false;
      runInAction(() => {
        this.name = name.trim().slice(0, 24);
        this.battle = battle;
        this.phase = 'lobby';
        this.applyBattle(battle);
      });
      await this.refresh();
      if (this.active(revision, login) && this.phase !== 'finished') this.connectSocket();
      return true;
    } catch (error) {
      runInAction(() => {
        if (revision === this.revision)
          this.error =
            error instanceof Error && error.name === 'BattleCategorySelectionError'
              ? error.message
              : 'Не удалось подключиться к батлу. Проверьте код комнаты и повторите попытку.';
      });
      return false;
    } finally {
      runInAction(() => {
        if (revision === this.revision) this.connecting = false;
      });
    }
  }
  connectSocket() {
    if (
      !this.code ||
      this.reconnectBlocked ||
      this.phase === 'finished' ||
      this.connection === 'connecting' ||
      this.connection === 'open'
    )
      return;
    const revision = this.revision,
      login = this.viewer(),
      code = this.code,
      serial = ++this.socketSerial;
    const active = () => this.active(revision, login, code) && serial === this.socketSerial;
    this.channel?.close();
    this.connection = 'connecting';
    try {
      this.channel = this.transport.open(code, {
        open: () => {
          runInAction(() => {
            if (active()) {
              this.connection = 'open';
              this.error = null;
            }
          });
        },
        event: (event) => {
          if (active()) this.receive(event);
        },
        close: () => {
          runInAction(() => {
            if (active()) {
              this.connection = 'closed';
              this.starting = false;
              this.readyPending = false;
              if (this.pendingAnswer)
                this.answerError = 'Ответ не подтверждён. Повторите запрос после восстановления связи.';
            }
          });
        },
        invalid: () => {
          runInAction(() => {
            if (active()) this.error = 'Не удалось прочитать событие батла. Обновите комнату.';
          });
        },
      });
    } catch {
      this.connection = 'closed';
      this.error = 'Не удалось подключить игровой канал.';
    }
  }
  start() {
    if (!this.canStart) return;
    this.starting = true;
    if (!this.channel?.send({ type: 'start', actionId: this.newActionId() })) {
      this.starting = false;
      this.connection = 'closed';
    }
  }
  clearError() {
    this.error = null;
  }
  receive(event: BattleEvent) {
    if (event.type === 'error' && ['ROOM_NOT_FOUND', 'ROOM_CLOSED'].includes(event.code ?? '')) {
      this.closeUnavailableRoom();
      return;
    }
    if (
      'questionId' in event &&
      event.questionId &&
      this.questionKey &&
      event.type !== 'round' &&
      event.questionId !== this.question?.id &&
      !this.questionKey.endsWith(`:${event.questionId}`)
    )
      return;
    if (event.type === 'round' && event.index < this.round + 1) return;
    if (event.type === 'round_result' && event.index !== this.round + 1) return;
    this.streamVersion += 1;
    if (event.type !== 'error') this.applyTiming(event);
    if (event.type === 'ack') {
      if (this.pendingAnswer?.actionId === event.actionId) {
        this.transport.writeAnswer?.(this.code, this.questionKey, {
          titleId: this.pendingAnswer.titleId,
          remainingMs: this.remainingMs,
        });
        this.answers = { ...this.answers, [this.selfId]: { titleId: this.pendingAnswer.titleId } };
        this.pendingAnswer = null;
        this.answerError = null;
      }
      return;
    }
    if (event.type === 'answer_received') return;
    if (event.type === 'state' || event.type === 'finished') {
      this.applyBattle(event.battle);
      // The legacy state event contains no question or accepted answers.
      if (event.type === 'state' && this.phase !== 'lobby' && this.phase !== 'finished') void this.refresh();
      return;
    }
    if (event.type === 'player') {
      if (event.battle) this.applyBattle(event.battle);
      if (event.login === this.selfId && ['ready', 'unready'].includes(event.event)) this.readyPending = false;
      if (!event.battle && this.battle && this.phase === 'lobby' && event.event === 'left') {
        if (event.login === this.battle.guestLogin)
          this.battle = { ...this.battle, guestLogin: undefined, guestReady: false, hostReady: false };
        else if (event.login === this.battle.hostLogin) this.applyBattle({ ...this.battle, status: 'aborted' });
      }
      if (!event.battle && this.battle && this.phase === 'lobby' && event.event === 'disconnected') {
        if (event.login === this.battle.guestLogin) this.battle = { ...this.battle, guestReady: false };
        if (event.login === this.battle.hostLogin) this.battle = { ...this.battle, hostReady: false };
      } else if (
        event.event === 'joined' &&
        this.battle?.status === 'lobby' &&
        event.login !== this.battle.hostLogin &&
        (!this.battle.guestLogin || this.battle.guestLogin === event.login)
      ) {
        this.battle = { ...this.battle, guestLogin: event.login };
      }
      if (event.event === 'answered' && this.players.some((player) => player.id === event.login)) {
        this.answers = {
          ...this.answers,
          [event.login]:
            event.login === this.selfId && this.pendingAnswer ? { titleId: this.pendingAnswer.titleId } : {},
        };
        if (event.login === this.selfId) {
          this.pendingAnswer = null;
          this.answerError = null;
        }
      }
      return;
    }
    if (event.type === 'round') {
      this.roundPhase = 'question';
      void this.loadRound(event.question, event.index);
      return;
    }
    if (event.type === 'round_result') {
      if (event.index - 1 === this.round && this.phase !== 'finished') {
        this.roundPhase = 'reveal';
        this.answers = event.answers;
        this.pendingAnswer = null;
        this.answerError = null;
        if (this.question) this.phase = 'reveal';
        this.tick();
      }
      return;
    }
    this.starting = false;
    this.readyPending = false;
    this.answerError = this.pendingAnswer ? 'Не удалось подтвердить ответ. Повторите запрос.' : null;
    this.error = this.pendingAnswer ? null : 'Сервер не принял действие. Обновите комнату и повторите попытку.';
  }
  private loadRound(source: QuestionSource, index: number): Promise<void> {
    if (this.phase === 'finished') return Promise.resolve();
    const key = `${index}:${source.id}`;
    if (this.roundRequest?.key === key) return this.roundRequest.request;
    if (key === this.questionKey && this.question) return Promise.resolve();
    const revision = this.revision,
      login = this.viewer(),
      code = this.code;
    if (key !== this.questionKey) {
      this.questionKey = key;
      this.question = null;
      this.answers = {};
      this.pendingAnswer = null;
      this.answerError = null;
      this.frameReady = false;
      this.remainingMs = 0;
      this.serverAnsweredCount = 0;
      this.correctTitleId = undefined;
    }
    this.round = Math.max(0, index - 1);
    this.phase = 'loading';
    this.error = null;
    const request = (async () => {
      await Promise.resolve();
      try {
        const question = await this.transport.question(source);
        runInAction(() => {
          if (this.active(revision, login, code) && key === this.questionKey && this.phase !== 'finished') {
            this.question = question;
            const saved = this.transport.readAnswer?.(this.code, this.questionKey);
            if (saved && question.optionTitleIds.includes(saved.titleId)) {
              this.answers = { ...this.answers, [this.selfId]: { titleId: saved.titleId } };
              this.remainingMs = saved.remainingMs;
            }
            this.phase = this.roundPhase === 'reveal' ? 'reveal' : 'question';
            this.tick();
            this.error = null;
          }
        });
        if (
          this.active(revision, login, code) &&
          key === this.questionKey &&
          this.phase !== 'finished' &&
          question.difficulty === undefined &&
          this.transport.difficulty
        ) {
          void this.transport.difficulty(question.id).then(
            (value) =>
              runInAction(() => {
                if (
                  this.active(revision, login, code) &&
                  key === this.questionKey &&
                  this.question?.id === question.id &&
                  this.phase !== 'finished'
                )
                  this.question = { ...this.question, difficulty: value };
              }),
            () => undefined,
          );
        }
      } catch {
        runInAction(() => {
          if (this.active(revision, login, code) && key === this.questionKey && this.phase !== 'finished')
            this.error = 'Не удалось загрузить вопрос батла. Повторите запрос.';
        });
      }
    })();
    this.roundRequest = { key, request };
    void request.finally(() => {
      if (this.roundRequest?.request === request) this.roundRequest = null;
    });
    return request;
  }
  private closeUnavailableRoom() {
    if (this.battle) this.applyBattle({ ...this.battle, status: 'aborted' });
    this.reconnectBlocked = true;
    this.error = 'Батл не найден или уже закрыт. Создайте новый батл.';
  }
  stopReconnect() {
    if (this.reconnectBlocked) return;
    this.reconnectBlocked = true;
    this.socketSerial++;
    this.channel?.close();
    this.channel = null;
    this.connection = 'idle';
    this.starting = false;
    this.readyPending = false;
    this.error = 'Не удалось восстановить связь с батлом. Повторите подключение.';
  }
  async retryConnection() {
    if (this.phase === 'finished') return;
    this.reconnectBlocked = false;
    this.retryGeneration++;
    this.error = null;
    await this.refresh();
    this.connectSocket();
  }
  markFrameReady() {
    if (this.phase === 'question' || this.phase === 'reveal') this.frameReady = true;
  }
  tick(now = Date.now()) {
    const time = now + this.offset;
    if ((this.phase === 'question' || this.phase === 'loading') && !this.answered && this.deadline)
      this.remainingMs = Math.max(0, this.deadline - time);
    if (this.phase === 'reveal') this.revealRemainingMs = Math.max(0, this.nextQuestionAt - time);
  }
  private newActionId() {
    return this.transport.actionId?.() ?? crypto.randomUUID();
  }
  private applyTiming(value: Partial<BattleSnapshot>) {
    if (value.serverNow && Date.parse(value.serverNow) > 0) this.offset = Date.parse(value.serverNow) - Date.now();
    if (value.questionDeadlineAt) this.deadline = Date.parse(value.questionDeadlineAt);
    if (value.nextQuestionAt) this.nextQuestionAt = Date.parse(value.nextQuestionAt);
    if (value.correctTitleId) this.correctTitleId = value.correctTitleId;
    if (value.answeredCount !== undefined) this.serverAnsweredCount = value.answeredCount;
    this.tick();
  }
  setReady(ready: boolean) {
    if (this.phase !== 'lobby' || this.connection !== 'open' || this.readyPending) return;
    this.readyPending = true;
    if (!this.channel?.send({ type: ready ? 'ready' : 'unready', actionId: this.newActionId() })) {
      this.readyPending = false;
      this.connection = 'closed';
    }
  }
  exit() {
    if (this.connection === 'open') this.channel?.send({ type: 'leave', actionId: this.newActionId() });
    this.leave();
  }
  answer(index: number) {
    const titleId = this.question?.optionTitleIds[index];
    if (
      !titleId ||
      this.phase !== 'question' ||
      !this.frameReady ||
      this.answered ||
      this.connection !== 'open' ||
      !this.remainingMs
    )
      return;
    this.pendingAnswer = { questionId: this.question!.id, titleId, actionId: this.newActionId() };
    this.answerError = null;
    if (!this.channel?.send({ type: 'answer', ...this.pendingAnswer })) {
      this.connection = 'closed';
      this.answerError = 'Ответ не подтверждён. Повторите запрос после восстановления связи.';
    }
  }
  retryAnswer(): Promise<void> {
    if (this.answerRetry) return this.answerRetry;
    const pending = this.pendingAnswer;
    const key = this.questionKey,
      revision = this.revision,
      login = this.viewer(),
      code = this.code;
    const request = (async () => {
      await this.refresh();
      if (
        !this.active(revision, login, code) ||
        !pending ||
        this.pendingAnswer !== pending ||
        this.questionKey !== key ||
        this.phase !== 'question' ||
        this.answers[this.selfId]
      )
        return;
      if (this.connection !== 'open') {
        this.connectSocket();
        return;
      }
      runInAction(() => {
        this.answerError = null;
        if (!this.channel?.send({ type: 'answer', ...pending }))
          this.answerError = 'Не удалось повторить ответ. Восстановите соединение.';
      });
    })();
    this.answerRetry = request;
    void request.finally(() => {
      if (this.answerRetry === request) this.answerRetry = null;
    });
    return request;
  }
  refresh(): Promise<void> {
    if (this.request) return this.request;
    if (!this.code) return Promise.resolve();
    const revision = this.revision,
      login = this.viewer(),
      code = this.code,
      version = this.streamVersion;
    this.syncing = true;
    const request = (async () => {
      await Promise.resolve();
      try {
        const state = await this.transport.snapshot(code);
        if (!this.active(revision, login, code) || version !== this.streamVersion) return;
        if (state.battle.inviteCode !== code || state.battle.id !== this.battle?.id)
          throw new Error('Battle snapshot mismatch');
        runInAction(() => this.applyBattle(state.battle));
        if (state.phase) this.roundPhase = state.phase;
        if (state.question && !finished(state.battle))
          await this.loadRound(state.question, state.roundIndex ?? state.battle.questionsPlayed + 1);
        if (!this.active(revision, login, code) || version !== this.streamVersion) return;
        runInAction(() => {
          this.applyTiming(state);
          if (state.phase === 'reveal' && this.phase !== 'finished') this.phase = 'reveal';
          const ownAnswer = this.answers[this.selfId];
          this.answers = {
            ...state.answers,
            ...(!state.answers[this.selfId] && ownAnswer ? { [this.selfId]: ownAnswer } : {}),
          };
          if (this.answers[this.selfId]) {
            this.pendingAnswer = null;
            this.answerError = null;
          }
          this.error = null;
        });
      } catch (error) {
        runInAction(() => {
          if (this.active(revision, login, code) && version === this.streamVersion) {
            const failure = error as { status?: number; data?: { code?: string; error?: string } };
            if (
              [403, 404, 410].includes(failure.status ?? 0) ||
              ['ROOM_NOT_FOUND', 'ROOM_CLOSED'].includes(failure.data?.code ?? failure.data?.error ?? '')
            )
              this.closeUnavailableRoom();
            else this.error = 'Не удалось обновить батл. Повторите запрос.';
          }
        });
      } finally {
        runInAction(() => {
          if (this.active(revision, login, code)) this.syncing = false;
          if (this.request === request) this.request = null;
        });
      }
    })();
    this.request = request;
    return request;
  }
  leave() {
    this.revision += 1;
    this.socketSerial += 1;
    this.channel?.close();
    this.channel = null;
    this.connection = 'idle';
    this.phase = 'setup';
    this.battle = null;
    this.question = null;
    this.questionKey = '';
    this.answers = {};
    this.error = null;
    this.answerError = null;
    this.connecting = false;
    this.syncing = false;
    this.starting = false;
    this.request = null;
    this.answerRetry = null;
    this.roundRequest = null;
    this.login = '';
    this.pendingAnswer = null;
    this.frameReady = false;
    this.deadline = 0;
    this.readyPending = false;
    this.serverAnsweredCount = undefined;
    this.correctTitleId = undefined;
    this.roundPhase = 'question';
    this.reconnectBlocked = false;
  }
}
